<template>
  <div class="d-flex flex-column-fluid eligible-customers-page">
    <div class="container">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Eligible Payers</h1>
            <p class="subtitle">View and manage eligible payers (customers and groups) for financing</p>
          </div>
          <div class="header-actions">
            <router-link 
              :to="{ name: 'business-insights', params: { business_id: businessId } }" 
              class="btn btn-dark btn-dashboard"
              v-if="businessId"
            >
              <i class="flaticon2-line-chart"></i>
              Back to Business Insights
            </router-link>
            <router-link 
              :to="{ name: 'financing' }" 
              class="btn btn-dark btn-dashboard"
              v-else
            >
              <i class="flaticon2-line-chart"></i>
              Back to Businesses
            </router-link>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading eligible payers...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Search Section -->
        <div class="search-section mb-4">
          <div class="search-wrapper">
            <div class="search-input-wrapper">
              <input 
                type="text" 
                class="form-control search-input" 
                placeholder="Search by payer name..." 
                v-model="searchTerm"
                @input="handleSearchInput"
              />
              <span v-if="searchTerm" class="clear-icon" @click="clearSearch">
                <i class="las la-times"></i>
              </span>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="isEmpty"
        >
          <i class="las la-users la-5x text-muted mb-3"></i>
          <div class="empty-title">
            <span v-if="searchTerm">No eligible payers found for "{{ searchTerm }}"</span>
            <span v-else>No eligible payers found</span>
          </div>
          <div class="empty-subtitle" v-if="searchTerm">
            Try adjusting your search terms
          </div>
        </div>

        <!-- Customers Table -->
        <financing-table
          v-if="!isEmpty && !error"
          :columns="tableColumns"
          :data="customers"
          :grid-template="'350px 150px 275px 275px 150px'"
          :current-page="currentPage"
          :total-rows="totalCustomers"
          :page-size="pageSize"
          :table-name="'eligible-customers'"
          :empty-title="getEmptyTitle()"
          :empty-subtitle="searchTerm ? 'Try adjusting your search terms' : ''"
          :empty-icon="'las la-users'"
          :row-class="getRowClass"
          :tooltip="getCustomerTooltip"
          :formatters="statusFormatters"
          @page-change="setPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import BasePagination from "@/components/UI/BasePagination.vue";
import FinancingTable from "@/components/UI/FinancingTable.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "EligibleCustomers",
  components: {
    BasePagination,
    FinancingTable
  },
  data() {
    return {
      loading: true,
      error: null,
      
      // Payers data (customers and groups)
      customers: [],
      totalCustomers: 0,
      currentPage: 1,
      pageSize: 20,
      businessId: null,
      
      // Search
      searchTerm: "",
      debounceTimer: null,
    };
  },
  
  computed: {
    isEmpty() {
      return this.customers.length === 0;
    },
    
    tableColumns() {
      return [
        {
          header: 'Payer',
          field: 'customer_name',
          type: 'text',
          align: 'text-center',
          class: 'customer-name-col',
          icon: 'las la-user'
        },
        {
          header: 'Currency',
          field: 'base_currency',
          type: 'currency-code',
          align: 'text-center',
          class: 'currency-col',
          icon: 'las la-coins'
        },
        {
          header: 'Lifetime Credit Used',
          field: 'total_disbursed_amount',
          type: 'currency',
          align: 'text-right',
          class: 'disbursed-amount-col',
          icon: 'las la-money-bill',
          currencyField: 'base_currency'
        },
        {
          header: 'Available Credit Limit',
          field: 'pending_balance',
          type: 'currency',
          align: 'text-right',
          class: 'disbursed-amount-col',
          icon: 'las la-money-bill',
          currencyField: 'base_currency'
        },
        {
          header: 'Status',
          field: 'is_active',
          type: 'status',
          align: 'text-center',
          class: 'status-col',
          icon: 'las la-toggle-on',
          formatter: 'activeStatus'
        }
      ];
    },
    
    statusFormatters() {
      return {
        activeStatus: (value) => value ? 'Active' : 'Inactive'
      };
    }
  },
  
  mounted() {
    // Set business ID from route
    this.businessId = this.$route.params.business_id || this.$route.query.business_id;
    this.loadCustomers();
  },
  
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  
  methods: {
    async loadCustomers(page = 1) {
      try {
        this.loading = true;
        this.error = null;
        
        const params = {
          page,
          page_size: this.pageSize,
        };
        
        if (this.searchTerm) {
          params.search = this.searchTerm;
        }
        
        // Use stored business ID (or get from route if not set)
        if (!this.businessId) {
          this.businessId = this.$route.params.business_id || this.$route.query.business_id;
        }
        
        const response = await FinancingService.getEligibleCustomers(this.businessId, params);
        
        if (response && response.data) {
          this.customers = this.formatCustomerData(response.data);
          // Use total count from API response for proper pagination
          this.totalCustomers = response.count || response.total || response.data.length || 0;
          this.currentPage = page;
        } else {
          this.customers = [];
          this.totalCustomers = 0;
        }
        
      } catch (error) {
        console.error('Error loading payers:', error);
        this.error = formatErrorMessage(error.message || 'Failed to load eligible payers');
        this.customers = [];
        this.totalCustomers = 0;
      } finally {
        this.loading = false;
      }
    },
    
    formatCustomerData(customers) {
      return customers.map(customer => {
        const isGroup = !!customer.customer_group__id;
        const payerId = isGroup ? customer.customer_group__id : customer.customer_id;
        const payerName = isGroup ? customer.customer_group_name : customer.customer_name;
        
        return {
          id: payerId || customer.id,
          customer_name: payerName || (isGroup ? `Unknown Group ${payerId}` : `Unknown Customer ${payerId}`),
          base_currency: customer.base_currency || customer.currency || "USD",
          total_disbursed_amount: customer.total_disbursed_amount || customer.disbursed_amount || 0,
          is_active: customer.is_active !== false, // Default to true if not present
          pending_balance: customer.pending_balance || 0,
          type: isGroup ? 'group' : 'customer',
          // Raw data for potential future use
          raw: customer,
        };
      });
    },
    
    handleSearchInput() {
      // Clear existing timer
      clearTimeout(this.debounceTimer);
      
      // Set new timer for debounced search
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadCustomers(1);
      }, 300);
    },
    
    clearSearch() {
      this.searchTerm = "";
      this.currentPage = 1;
      this.loadCustomers(1);
    },
    
    setPage(page, pageSize) {
      // If pageSize is provided and different from current, update it
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      
      // Fetch customers for the requested page
      if (page >= 1) {
        this.loadCustomers(page);
      }
    },
    
    formatCurrency(amount, currency) {
      const currencyToUse = currency || 'USD';
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, currencyToUse);
      return this.$formatCurrency(amount, currencyToUse);
    },
    
    getRowClass(customer) {
      return customer.is_active ? '' : 'inactive';
    },
    
    getCustomerTooltip(customer) {
      return customer.is_active ? null : 'This customer is inactive';
    },
    
    getEmptyTitle() {
      return this.searchTerm ? `No eligible payers found for "${this.searchTerm}"` : 'No eligible payers found';
    },
  },
};
</script>

<style scoped lang="scss">
@import './index.scss';

// Additional styles specific to eligible customers
.eligible-customers-page {
  .search-section {
    .search-wrapper {
      .search-input-wrapper {
        position: relative;
        max-width: 400px;
        
        .search-input {
          padding: 0.6rem 2.5rem 0.6rem 1rem;
          border: 1px solid #E5E5E5;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          
          &:focus {
            border-color: #1e293b;
            box-shadow: 0 0 0 0.2rem rgba(30, 41, 59, 0.1);
          }
        }
        
        .clear-icon {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #6c757d;
          font-size: 1.1rem;
          
          &:hover {
            color: #1e293b;
          }
        }
      }
    }
  }
  

}


</style> 